import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import Interaction from "../../../models/interactions/Interaction";
import { ISendActorToPosition } from "../../../models/interactions/SendActorToPosition";
import NodeActorDropdown from "../components/NodeActorDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeToggle from "../components/NodeToggle";
import NodePositionDropdown from "../components/NodePositionDropdown";
import NodeNumberField from "../components/NodeNumberField";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function SendActorToPositionNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as ISendActorToPosition;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "actor";

  const actorDropdown = useMemo(
    () => (
      <NodeActorDropdown
        label="Actor"
        selectedActorId={interaction.npcId}
        onClick={(actorId) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            npcId: actorId,
          } as ISendActorToPosition)
        }
      />
    ),
    [interaction.npcId, updateInteraction, props.nodeId],
  );

  // position id
  const positionDropdown = useMemo(
    () => (
      <NodePositionDropdown
        label="Position"
        selectedPositionId={interaction.positionId}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-position-on-map-button"
            className={
              NODE_PICK_BUTTON_BASE +
              (isPickerActiveForNode
                ? NODE_PICK_BUTTON_ACTIVE
                : NODE_PICK_BUTTON_IDLE)
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "actor",
                  title: "Pick actor position",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
        onClick={(positionId) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            positionId,
          } as ISendActorToPosition)
        }
      />
    ),
    [
      dispatch,
      interaction,
      isPickerActiveForNode,
      props.nodeId,
      updateInteraction,
    ],
  );

  const teleport = useMemo(
    () => (
      <NodeToggle
        label="Teleport"
        value={interaction.teleport}
        onChange={(teleport) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            teleport,
          } as ISendActorToPosition)
        }
      />
    ),
    [interaction.teleport, updateInteraction, props.nodeId],
  );

  const waitForObjectReached = useMemo(
    () => (
      <NodeToggle
        label="Wait for Object Reached"
        value={interaction.waitForObjectReached}
        onChange={(waitForObjectReached) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            waitForObjectReached,
          } as ISendActorToPosition)
        }
      />
    ),
    [interaction.waitForObjectReached, updateInteraction, props.nodeId],
  );

  const moveSpeedOverride = useMemo(
    () => (
      <NodeNumberField
        label="Move Speed Override"
        value={interaction.moveSpeedOverride ?? 0}
        onChange={(moveSpeedOverride) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            moveSpeedOverride,
          } as ISendActorToPosition)
        }
      />
    ),
    [interaction, props.nodeId, updateInteraction],
  );

  return (
    <div className="w-full h-full flex flex-col gap-[12px]">
      {actorDropdown}
      {positionDropdown}
      {moveSpeedOverride}
      {teleport}
      {waitForObjectReached}
    </div>
  );
}
